// Client-side image optimisation for gallery uploads. Gallery photos are
// served straight from the R2 public domain with no CDN resizing, so the
// portal shrinks them before upload: a display variant (max 1920px WebP) and
// a thumbnail (max 480px WebP). The full-res original is not kept — a phone
// photo never reaches the visitor at 15MB. If Cloudflare image transforms are
// layered on later (needs the custom R2 domain), these keys keep working.

export interface PreparedImage {
  displayFile: File;
  thumbFile: File | null;
  /** Display-variant pixel dimensions, when the file could be decoded. */
  width: number | null;
  height: number | null;
}

const DISPLAY_MAX_PX = 1920;
const THUMB_MAX_PX = 480;
const WEBP_QUALITY = 0.82;

interface Encoded {
  blob: Blob;
  width: number;
  height: number;
  scaled: boolean;
}

function encodeWebp(bitmap: ImageBitmap, maxPx: number): Promise<Encoded> {
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Canvas 2D context unavailable"));
  context.drawImage(bitmap, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({ blob, width, height, scaled: scale < 1 });
        } else {
          reject(new Error("Image encoding failed"));
        }
      },
      "image/webp",
      WEBP_QUALITY
    );
  });
}

function webpName(originalName: string, suffix = ""): string {
  const base = originalName.replace(/\.[^.]+$/, "").trim() || "image";
  return `${base}${suffix}.webp`;
}

/**
 * Prepare a gallery upload: decode (EXIF orientation applied), downscale to
 * the display + thumbnail variants. Files the browser can't decode are passed
 * through untouched (the API still validates type/size). Animated GIFs keep
 * their original bytes as the display variant — canvas would flatten them to
 * one frame — but still get a static thumbnail.
 */
export async function prepareGalleryImage(file: File): Promise<PreparedImage> {
  // SVG is vector — rasterising it to WebP would throw away its scalability and
  // bloat it. Upload the original bytes untouched; it serves as its own thumbnail.
  if (file.type === "image/svg+xml" || /\.svg$/i.test(file.name)) {
    return { displayFile: file, thumbFile: null, width: null, height: null };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return { displayFile: file, thumbFile: null, width: null, height: null };
  }

  try {
    const thumb = await encodeWebp(bitmap, THUMB_MAX_PX);
    const thumbFile = new File([thumb.blob], webpName(file.name, "-thumb"), {
      type: "image/webp"
    });

    const isGif = file.type === "image/gif" || /\.gif$/i.test(file.name);
    if (isGif) {
      return { displayFile: file, thumbFile, width: bitmap.width, height: bitmap.height };
    }

    const display = await encodeWebp(bitmap, DISPLAY_MAX_PX);
    // An already-small, well-compressed file can re-encode *larger* — keep the
    // original bytes when no downscale happened and WebP didn't help.
    if (!display.scaled && display.blob.size >= file.size) {
      return { displayFile: file, thumbFile, width: bitmap.width, height: bitmap.height };
    }

    const displayFile = new File([display.blob], webpName(file.name), { type: "image/webp" });
    return { displayFile, thumbFile, width: display.width, height: display.height };
  } finally {
    bitmap.close();
  }
}
